import { BreakpointObserver, MediaMatcher } from "@angular/cdk/layout";
import { Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation } from "@angular/core";
import { Subscription } from "rxjs";
import { MatSidenav } from "@angular/material/sidenav";
import { CoreService } from "../../../../../shared/src/lib/services/core.service";
import { navItems } from "./vertical/sidebar/sidebar-data";
import { NgScrollbarModule } from "ngx-scrollbar";
import { AppSearchDialogComponent, HeaderComponent } from "./vertical/header/header.component";
import { AppHorizontalHeaderComponent } from "./horizontal/header/header.component";
import { AppHorizontalSidebarComponent } from "./horizontal/sidebar/sidebar.component";
import { SidebarComponent } from "./vertical/sidebar/sidebar.component";
import { CustomizerComponent } from "./shared/customizer/customizer.component";
import { RouterModule } from "@angular/router";
import { CommonModule } from "@angular/common";
import { AppNavItemComponent } from "./vertical/sidebar/nav-item/nav-item.component";
import { MaterialModule } from "../../material.module";
import { NavService } from "../../../../../shared/src/lib/services/nav.service";
import type { AppSettings } from "../../app.config";
import { ApiService, isPasswordExpired } from "../../../../../shared/src/lib/services/api.service";
import { SessionTimeoutService } from "../../../../../shared/src/lib/services/session-timeout.service";

const MOBILE_VIEW = "screen and (max-width: 768px)";
const TABLET_VIEW = "screen and (min-width: 769px) and (max-width: 1024px)";
const MONITOR_VIEW = "screen and (min-width: 1024px)";
const BELOWMONITOR = "screen and (max-width: 1023px)";
@Component({
    selector: "app-full",
    templateUrl: "./full.component.html",
    imports: [
        NgScrollbarModule,
        HeaderComponent,
        AppHorizontalHeaderComponent,
        AppHorizontalSidebarComponent,
        SidebarComponent,
        AppSearchDialogComponent,
        CustomizerComponent,
        MaterialModule,
        RouterModule,
        CommonModule,
        AppNavItemComponent,
    ],
    styleUrls: [],
    styles: [
      `
        .password-expired-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 12px 24px;
          background-color: #fffbeb;
          border-bottom: 1px solid #fde68a;
          color: #92400e;
          font-size: 14px;
          font-weight: 600;
        }
        .password-expired-bar .notice {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .password-expired-bar mat-icon {
          color: #d97706;
        }
      `,
    ],
    encapsulation: ViewEncapsulation.None
})
export class FullComponent implements OnInit, OnDestroy {
  navItems = navItems;
  @ViewChild("leftsidenav")
  public sidenav: MatSidenav;
  resView = false;
  //get options from service
  options = this.settings.getOptions();
  navopt = this.navService.showClass;
  private readonly layoutChangesSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private readonly htmlElement!: HTMLHtmlElement;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  /** While the password has expired, menus and navigation are hidden: only change-password is available. */
  get passwordExpired(): boolean {
    return isPasswordExpired();
  }

  logout() {
    this.apiService.logout();
  }

  constructor(
    private readonly settings: CoreService,
    private readonly mediaMatcher: MediaMatcher,
    private readonly navService: NavService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly apiService: ApiService,
    private readonly sessionTimeOut: SessionTimeoutService,
  ) {
    this.htmlElement = document.querySelector("html")!;
    this.layoutChangesSubscription = this.breakpointObserver.observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR]).subscribe((state) => {
      // SidenavOpened must be reset true when layout changes
      this.options.sidenavOpened = true;
      this.isMobileScreen = state.breakpoints[BELOWMONITOR];

      if (!this.options.sidenavCollapsed) {
        this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
      }
      this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
      this.resView = state.breakpoints[BELOWMONITOR];
    });

    // Initialize project theme with options
    this.receiveOptions(this.options);
  }

  ngOnInit(): void {
    this.validItems();
    this.sessionTimeOut.setupSessionTimeout();
  }

  private readonly PERMISSION_NAV_MAP: { permission: string; displayNames: string[] }[] = [
    { permission: "canSetCommission", displayNames: ["Commission"] },
    { permission: "canSetTakeRisk", displayNames: ["Risk Taking"] },
    { permission: "canConfigureCountry", displayNames: ["Supported Countries"] },
    { permission: "canConfigureCurrency", displayNames: ["Exchange Rate"] },
    { permission: "canConfigureSport", displayNames: ["Supported Sports"] },
    { permission: "canSetupBetFairAccounts", displayNames: ["Betfair Accounts"] },
    { permission: "canViewReports", displayNames: ["Reports", "Activity Report"] },
    { permission: "canEnableWhiteLabel", displayNames: ["White Labels"] },
    { permission: "canEnableLiveStream", displayNames: ["Live Stream"] },
    { permission: "canEnableLiveScore", displayNames: ["Live Score"] },
    { permission: "canSendNotifications", displayNames: ["Notificaions"] },
    { permission: "canViewPL", displayNames: ["Live PnL"] },
    { permission: "enableSupportChannels", displayNames: ["Customer Support"] },
  ];

  validItems() {
    const udata = this.apiService.getUserData();
    const role = udata.ROLE;
    const permissions: string[] = udata.PERMISSIONS.split(",");

    let items = navItems.filter((item) => item.displayName == "Home" || item.displayName == "Dashboard");

    for (const { permission, displayNames } of this.PERMISSION_NAV_MAP) {
      if (permissions.includes(permission)) {
        items = [...items, ...navItems.filter((item) => displayNames.includes(item.displayName as string))];
      }
    }

    if (permissions.includes("canHandleFunds")) {
      const fundNames = ["Deposit", "Withdraw", "Transfer", "Wallets"];
      items = [...items, ...navItems.filter((item) => item.navCap == "Funds" || fundNames.includes(item.displayName as string))];
    }

    if (role == "super_admin") {
      const superAdminNames = ["Roles", "Bet Status", "Bet Info", "Betting P/L", "Payments", "VPN Whitelist"];
      items = [...items, ...navItems.filter((item) => superAdminNames.includes(item.displayName as string))];
    }

    if (permissions.includes("canViewTransactions") && role !== "super_admin") {
      items = [...items, ...navItems.filter((item) => item.displayName == "Payments")];
    }

    if (permissions.includes("canViewRegistrationReport") && this.isAdminOrSuperMaster(role)) {
      items = [...items, ...navItems.filter((item) => item.displayName === "Registered Player Report")];
    }

    if (this.isAdminOrOperatorOfAdmin(role, udata.PARENT_ROLE)) {
      items = [...items, ...navItems.filter((item) => item.navCap == "Settings")];
    }

    this.navItems = items;
  }

  private isAdminOrSuperMaster(role: string): boolean {
    return role === "admin" || role === "super_master";
  }

  private isAdminOrOperatorOfAdmin(role: string, parentRole: string): boolean {
    return role === "admin" || (role === "operator" && parentRole === "admin");
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
    this.navItems = navItems;
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  receiveOptions(options: AppSettings): void {
    this.options = options;
    this.toggleDarkTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === "dark") {
      this.htmlElement.classList.add("dark-theme");
      this.htmlElement.classList.remove("light-theme");
    } else {
      this.htmlElement.classList.remove("dark-theme");
      this.htmlElement.classList.add("light-theme");
    }
  }
}
